'use client';
import PaginatedContent from '../components/PaginatedContent';

import LibraryFolderDialog, {FolderIcon} from '../components/LibraryFolders';
import LibraryBulkDialog from '../components/LibraryBulkDialog';
import {sortLibraryDecks,toggleDeckSelection} from '@/lib/library-selection';
import LibrarySelectionActions from '../components/LibrarySelectionActions';
import LibraryExplorerEntries from '../components/LibraryExplorerEntries';
import Button from '../components/Button';
import Pagination from '../components/Pagination';
import {explorerPage} from '@/lib/library-explorer';
import {LibraryDragDropProvider,LibraryFolderDropTarget} from '../components/LibraryDragDrop';
import {listLibraryFolders, type LibraryFolder} from '@/lib/library-workspace';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useUrlParam } from '@/app/hooks/useUrlState';
import Link from 'next/link';
import { usePostHog } from 'posthog-js/react';
import { useAuth } from '../components/AuthProvider';
import { useSubscription } from '../components/SubscriptionProvider';
import { getSavedDecks, deleteSavedDeck } from '../../lib/auth';
import type { SavedDeckSummary } from '../../lib/supabase';
import { flashSaved } from '../components/SaveToast';
import { captureError } from '../../lib/error-tracking';
import { ConfirmModal } from '../components/ConfirmModal';


export default function LibraryPage() {
  const { user, loading, openSignIn } = useAuth();
  const { tier, refreshSubscription } = useSubscription();
  const posthog = usePostHog();
  const [decks, setDecks] = useState<SavedDeckSummary[]>([]);
  const [loadingDecks, setLoadingDecks] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deckToDelete, setDeckToDelete] = useState<string | null>(null);
  const [sortBy, setSortBy] = useUrlParam('sort', 'modified-desc', {values:['modified-desc','modified-asc','newest','oldest','name-az','name-za','most-cards','fewest-cards']});
  const hasTrackedView = useRef(false);
  const [folder, setFolder] = useUrlParam('folder', 'main');
  const [itemPage,setItemPage] = useUrlParam('page',0,{max:100001});
  const [folders, setFolders] = useState<LibraryFolder[]>([]);
  const [folderError, setFolderError] = useState('');
  const [loadingFolders,setLoadingFolders] = useState(true);
  const [folderDialog,setFolderDialog] = useState<{mode:'create'|'rename'|'delete';folder?:LibraryFolder}|null>(null);
  const selectionScope=[user?.id,folder].join(':');
  const [selection,setSelection]=useState<{scope:string;ids:string[]}>({scope:'',ids:[]});
  const [bulkDialog,setBulkDialog]=useState<{action:'move'|'delete';decks:SavedDeckSummary[]}|null>(null);
  useEffect(()=>{setSelection({scope:selectionScope,ids:[]});setBulkDialog(null);},[selectionScope]);
  const selectedIds=selection.scope===selectionScope?selection.ids.filter(id=>decks.some(deck=>deck.id===id)):[];
  function selectDecks(ids:string[],checked:boolean){setSelection({scope:selectionScope,ids:toggleDeckSelection(selectedIds,ids,checked)});}
  const isMain=['main','all','none'].includes(folder);
  const folderName=isMain?'Main':folders.find(f=>f.id===folder)?.name||'Folder';
  const visibleFolders=isMain?[...folders].sort((a,b)=>(sortBy==='name-za'?-1:1)*a.name.localeCompare(b.name,undefined,{numeric:true,sensitivity:'base'})):[];
  const openFolder=(id:string)=>{setFolder(id);setItemPage(0);};
  const folderCounts=useMemo(()=>decks.reduce<Record<string,number>>((counts,deck)=>{counts.all++;const key=deck.folder_id||'none';counts[key]=(counts[key]||0)+1;return counts;},{all:0,none:0}),[decks]);
  async function refreshFolders() { setFolders(await listLibraryFolders()); setLoadAttempt(n=>n+1); }
  useEffect(()=>{let active=true;setLoadingFolders(true);if(user)listLibraryFolders().then(rows=>{if(active){setFolders(rows);setFolderError('');}}).catch(()=>{if(active)setFolderError('Could not load folders. Reload to try again.');}).finally(()=>{if(active)setLoadingFolders(false);});else{setFolders([]);setLoadingFolders(false);}return()=>{active=false;};},[user]);


  // Derive filtered + sorted decks
  const filteredDecks = useMemo(() => {
    const result = decks.filter(d => isMain ? !d.folder_id : d.folder_id === folder);
    return sortLibraryDecks(result,sortBy);
  }, [decks, sortBy, folder, isMain]);
  const items=explorerPage(visibleFolders,filteredDecks,itemPage);
  const pageDeckIds=items.decks.map(deck=>deck.id);
  useEffect(()=>{if(!loadingDecks&&!loadingFolders&&itemPage!==items.page)setItemPage(items.page);},[loadingDecks,loadingFolders,itemPage,items.page,setItemPage]);


  useEffect(() => {
    async function loadDecks() {
      setLoadingDecks(true);
      setLoadError(null);
      try {
        if (user) {
          const savedDecks = await getSavedDecks(user.id);
          setDecks(savedDecks);

          // Track successful library loads, including an empty library.
          if (!hasTrackedView.current) {
            hasTrackedView.current = true;
            const totalCards = savedDecks.reduce((sum, d) => sum + d.card_count, 0);
            posthog?.capture('library_viewed', {
              deck_count: savedDecks.length,
              total_cards: totalCards,
              user_tier: tier,
            });
          }
        }
      } catch (err) {
        captureError('library_load_error', err, { user_id: user?.id });
        setLoadError('We could not load your library. Your decks are still safe.');
        posthog?.capture('library_load_failed', {
          error_message: err instanceof Error ? err.message : 'Unknown error',
        });
      } finally {
        setLoadingDecks(false);
      }
    }

    if (!loading) {
      loadDecks();
    }
  }, [user, loading, posthog, tier, loadAttempt]);

  const handleDeleteClick = (deckId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDeckToDelete(deckId);
  };

  const handleDeleteConfirm = async () => {
    if (!deckToDelete || !user) return;

    const deckInfo = decks.find(d => d.id === deckToDelete);
    setDeletingId(deckToDelete);
    setDeckToDelete(null);
    try {
      await deleteSavedDeck(deckToDelete, user.id);
      setDecks(decks.filter(d => d.id !== deckToDelete));
      flashSaved('Deck deleted');
      refreshSubscription();
      posthog?.capture('deck_deleted', {
        deck_id: deckToDelete,
        card_count: deckInfo?.card_count || 0,
      });
    } catch (err) {
      captureError('deck_delete_error', err, {
        deck_id: deckToDelete,
        deck_name: deckInfo?.file_name,
        card_count: deckInfo?.card_count,
      });
      flashSaved('Failed to delete deck');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDeckDate = (deck: SavedDeckSummary) => {
    const created = new Date(deck.created_at).getTime();
    const updated = new Date(deck.updated_at).getTime();
    const wasUpdated = updated - created > 5000; // more than 5 seconds apart
    if (wasUpdated) {
      return `Updated ${formatDate(deck.updated_at)}`;
    }
    return `Created ${formatDate(deck.created_at)}`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background py-12 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 w-48 bg-muted-bg rounded mb-8" />
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-muted-bg rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Not signed in - show soft lock
  if (!user) {
    return (
      <div className="min-h-screen bg-background py-12 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-bold mb-8 font-serif">Your Deck Library</h1>

          <div className="text-center py-16 px-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-6">
              <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
              </svg>
            </div>

            <h2 className="text-xl font-semibold mb-2">Keep your decks in one place</h2>
            <p className="text-muted mb-6 max-w-sm mx-auto">
              Sign in to save reviewed decks to your personal library and access them across devices.
            </p>

            <button
              onClick={() => openSignIn()}
              className="ui-button ui-button--primary"
            >
              Sign in with Google
            </button>

            <p className="text-xs text-muted mt-8">
              Save your reviewed decks in one place, with no saved-deck limit.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main contains loose lectures and flat folders; opening a folder shows only its contents.
  return <div className="min-h-screen bg-background px-5 py-12 sm:px-8"><div className="mx-auto max-w-6xl">
    <div className="mb-8 flex items-center justify-between gap-3"><h1 className="font-serif text-2xl font-bold sm:text-3xl">Your Library</h1><Link href="/upload" className="ui-button ui-button--primary">Upload lectures</Link></div>
    {folderError&&<p role="alert" className="mb-3 text-sm text-danger">{folderError}</p>}
    <LibraryDragDropProvider key={user.id} folders={folders} onMoved={(item,folderId)=>{
      if(item.kind==='deck')setDecks(current=>current.map(deck=>deck.id===item.id?{...deck,folder_id:folderId||null}:deck));
    }}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <nav aria-label="Library location" className="flex min-w-0 items-center gap-2 text-sm">
          <LibraryFolderDropTarget folderId="" className="shrink-0"><button type="button" onClick={()=>openFolder('main')} aria-current={isMain?'page':undefined} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted-bg"><FolderIcon open={isMain}/>Main</button></LibraryFolderDropTarget>
          {!isMain&&<><span aria-hidden="true" className="text-muted">/</span><h2 className="truncate font-medium" title={folderName}>{folderName}</h2></>}
        </nav>
        {isMain&&<Button variant="secondary" onClick={()=>setFolderDialog({mode:'create'})}>New folder</Button>}
      </div>
      <section aria-label="Folder contents" aria-busy={loadingDecks} className="min-w-0">
        {loadingDecks&&decks.length===0?<p role="status" className="py-8 text-sm text-muted">Loading your library…</p>:loadError?<div role="alert" className="py-8 text-sm text-danger">{loadError}<Button variant="ghost" onClick={()=>setLoadAttempt(n=>n+1)}>Retry</Button></div>:<>
          <p className="mb-3 text-xs text-muted">{visibleFolders.length>0?`${visibleFolders.length} ${visibleFolders.length===1?'folder':'folders'} · `:''}{filteredDecks.length} {filteredDecks.length===1?'deck':'decks'}</p>
          {visibleFolders.length||filteredDecks.length?<PaginatedContent resetKey={`${folder}:${sortBy}:${decks.length}:${folders.length}`} label="Library entries"><LibraryExplorerEntries folders={items.folders} decks={items.decks} allFolders={folders} counts={folderCounts} selectedIds={selectedIds} onSelectPage={checked=>selectDecks(pageDeckIds,checked)} selectionActions={<LibrarySelectionActions count={selectedIds.length}
              onMove={()=>{if(selectedIds.length)setBulkDialog({action:'move',decks:decks.filter(deck=>selectedIds.includes(deck.id))});}}
              onDelete={()=>{if(selectedIds.length)setBulkDialog({action:'delete',decks:decks.filter(deck=>selectedIds.includes(deck.id))});}}
              onClear={()=>setSelection({scope:selectionScope,ids:[]})}/>} onSelect={(id,checked)=>selectDecks([id],checked)} sort={sortBy} onSort={value=>{setSortBy(value as typeof sortBy);setItemPage(0);}}
            onOpenFolder={openFolder} onRenameFolder={folder=>setFolderDialog({mode:'rename',folder})} onDeleteFolder={folder=>setFolderDialog({mode:'delete',folder})}
            onDeleteDeck={setDeckToDelete} onMoved={()=>setLoadAttempt(n=>n+1)} deletingId={deletingId}/></PaginatedContent>:<div className="rounded-xl border border-dashed border-border px-6 py-12 text-center">
            <p className="text-sm text-muted">{isMain?'Main is empty.':'This folder is empty.'}</p>
            {!isMain&&<p className="mt-2 text-xs text-muted">Drag saved decks onto this folder from Main, or choose Move from a deck’s menu.</p>}
          </div>}
          <Pagination page={items.page} totalPages={items.totalPages} hasNext={items.page+1<items.totalPages} onPageChange={setItemPage} className="mt-4 justify-end gap-2" label="Library pages"/>
        </>}
      </section>
    </LibraryDragDropProvider>
    {bulkDialog&&<LibraryBulkDialog {...bulkDialog} folders={folders} onClose={()=>setBulkDialog(null)} onDone={(ids,target)=>{
      const changed=new Set(ids);
      setDecks(current=>bulkDialog.action==='delete'?current.filter(deck=>!changed.has(deck.id)):current.map(deck=>changed.has(deck.id)?{...deck,folder_id:target||null}:deck));
      setSelection({scope:selectionScope,ids:[]});setLoadAttempt(n=>n+1);void refreshSubscription();
    }}/>}
    {folderDialog&&<LibraryFolderDialog {...folderDialog} onClose={()=>setFolderDialog(null)} onSaved={async id=>{
      await refreshFolders();
      if(folderDialog.mode==='create')openFolder(id);
      if(folderDialog.mode==='delete'&&folderDialog.folder?.id===folder)openFolder('main');
    }}/>}
    <ConfirmModal isOpen={!!deckToDelete} onConfirm={handleDeleteConfirm} onCancel={()=>setDeckToDelete(null)} title="Delete deck?" message="This will permanently delete this deck from your library. This cannot be undone." cancelLabel="Cancel" confirmLabel="Delete" variant="danger"/>
  </div></div>;
}
